import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api'
import { setAuthToken, removeAuthToken } from '@/api/client'
import { useCartStore } from './cart'
import { useWishlistStore } from './wishlist'

/**
 * Authentication Store
 * 
 * Manages user authentication state and profile
 */

const USER_STORAGE_KEY = 'inversun-user'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  const isInitialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userName = computed(() => {
    if (user.value?.first_name && user.value?.last_name) {
      return `${user.value.first_name} ${user.value.last_name}`
    }
    return user.value?.name || ''
  })
  const userEmail = computed(() => user.value?.email || '')

  const extractUser = (payload) => {
    if (!payload || typeof payload !== 'object') return null

    if (payload.user && typeof payload.user === 'object') {
      return payload.user
    }

    if (payload.customer && typeof payload.customer === 'object') {
      return payload.customer
    }

    if (payload.data && typeof payload.data === 'object') {
      if (payload.data.user && typeof payload.data.user === 'object') {
        return payload.data.user
      }

      if (payload.data.customer && typeof payload.data.customer === 'object') {
        return payload.data.customer
      }

      if (payload.data.data && typeof payload.data.data === 'object') {
        return payload.data.data
      }

      return payload.data
    }

    return null
  }

  const normalizeError = (err, fallback) => {
    const responseData = err?.response?.data
    const validationErrors = responseData?.errors

    if (typeof responseData?.message === 'string' && responseData.message.trim()) {
      return responseData.message
    }

    if (Array.isArray(validationErrors)) {
      return validationErrors[0] || fallback
    }

    if (validationErrors && typeof validationErrors === 'object') {
      const firstError = Object.values(validationErrors).flat().find(Boolean)
      if (firstError) return String(firstError)
    }

    if (typeof err?.message === 'string' && err.message.trim()) {
      return err.message
    }

    return fallback
  }

  // Actions
  async function init() {
    if (isInitialized.value) return
    
    // Try to load user from localStorage
    if (typeof window !== 'undefined') {
      try {
        const savedUser = localStorage.getItem(USER_STORAGE_KEY)
        if (savedUser) {
          user.value = JSON.parse(savedUser)
          // Validate token by fetching user (don't block init)
          fetchUser().catch(err => {
            console.warn('Token validation failed:', err)
          })
        }
      } catch (e) {
        console.warn('Failed to load user from storage:', e)
      }
    }
    
    isInitialized.value = true
  }

  function setUser(userData, token = null) {
    user.value = userData
    if (token) {
      setAuthToken(token)
    }
    if (typeof window !== 'undefined' && userData) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData))
    }
  }

  function clearUser() {
    user.value = null
    removeAuthToken()
    if (typeof window !== 'undefined') {
      localStorage.removeItem(USER_STORAGE_KEY)
    }
  }

  async function register(userData) {
    isLoading.value = true
    error.value = null

    try {
      const response = await authApi.register(userData)
      const nextUser = extractUser(response)
      if (!nextUser) {
        throw new Error('Registration succeeded but user payload is missing')
      }

      setUser(nextUser, response.data?.token || response.token)
      
      return response
    } catch (err) {
      error.value = normalizeError(err, 'Registration failed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function login(credentials) {
    isLoading.value = true
    error.value = null

    try {
      const response = await authApi.login(credentials)
      const nextUser = extractUser(response)
      if (!nextUser) {
        throw new Error('Login succeeded but user payload is missing')
      }

      setUser(nextUser, response.data?.token || response.token)
      
      // Fetch cart and wishlist in background (don't block login)
      const cartStore = useCartStore()
      const wishlistStore = useWishlistStore()
      
      // Fire and forget - don't await these
      cartStore.fetchCart().catch(err => console.warn('Cart fetch failed:', err))
      wishlistStore.fetchWishlist().catch(err => console.warn('Wishlist fetch failed:', err))
      
      return response
    } catch (err) {
      error.value = normalizeError(err, 'Invalid credentials')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    isLoading.value = true
    
    try {
      await authApi.logout()
    } catch (err) {
      console.warn('Logout API error:', err)
    } finally {
      clearUser()
      
      // Clear cart and wishlist
      const cartStore = useCartStore()
      const wishlistStore = useWishlistStore()
      cartStore.clearCart()
      wishlistStore.clearWishlist()
      
      isLoading.value = false
    }
  }

  async function refreshToken() {
    try {
      await authApi.refreshToken()
      return true
    } catch (err) {
      console.warn('Token refresh failed:', err)
      // If refresh fails, clear the user session
      clearUser()
      throw err
    }
  }

  async function fetchUser() {
    try {
      const response = await authApi.getUser()
      const nextUser = extractUser(response)
      if (!nextUser) {
        throw new Error('Failed to parse authenticated user payload')
      }

      setUser(nextUser)
      return nextUser
    } catch (err) {
      // Token is invalid, clear it
      clearUser()
      throw err
    }
  }

  async function forgotPassword(email) {
    isLoading.value = true
    error.value = null

    try {
      return await authApi.forgotPassword(email)
    } catch (err) {
      error.value = normalizeError(err, 'Unable to send reset link')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function resetPassword(data) {
    isLoading.value = true
    error.value = null

    try {
      return await authApi.resetPassword(data)
    } catch (err) {
      error.value = normalizeError(err, 'Unable to reset password')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    user,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    userName,
    userEmail,
    // Actions
    init,
    register,
    login,
    logout,
    refreshToken,
    fetchUser,
    forgotPassword,
    resetPassword,
    setUser,
    clearUser
  }
})
